import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { isGygExperiencesEnabled } from '@/config/bookingExperience';
import { buildGygDeepLinkUrl } from '@/lib/booking/buildGygDeepLinkUrl';
import { openGygInNewTab } from '@/lib/booking/openGygInNewTab';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_EXPERIENCE_OUTBOUND_CLICK,
  EVENT_EXPERIENCE_OUTBOUND_POPUP_BLOCKED,
} from '@/lib/analyticsEvents';
import {
  buildBookingCampaignRef,
  resolveBookingCampaignRefContext,
  type BookingCampaignSurface,
} from '@/utils/booking/buildBookingCampaignRef';
import type { ExperienceOutboundAnalyticsProps } from '@/lib/analyticsAttribution';
import { getReferralCreatorIdForApi } from '@/lib/analyticsAttribution';

export interface OpenGygDeepLinkParams {
  searchQuery: string;
  userId?: string;
  postId?: string;
  placeId?: string;
  googlePlaceId?: string;
  surface: BookingCampaignSurface;
  locationName?: string;
  contentType?: 'itinerary' | 'list';
  /** When true, caller already fired Experience Outbound Click (e.g. before auth gate). */
  skipAnalytics?: boolean;
}

function buildGygOutboundAnalyticsProps(
  params: OpenGygDeepLinkParams,
  searchQuery: string,
): ExperienceOutboundAnalyticsProps {
  const { refType, refId } = resolveBookingCampaignRefContext({
    postId: params.postId,
    placeId: params.placeId,
  });

  const cmp = buildBookingCampaignRef({
    userId: params.userId,
    refType,
    refId,
    surface: params.surface,
    vendor: 'gyg',
  });

  const referralCreatorId = getReferralCreatorIdForApi();

  return {
    surface: params.surface,
    vendor: 'gyg',
    search_query: searchQuery,
    cmp,
    ...(params.userId && { user_id: params.userId }),
    ...(refType === 'post' && refId && { post_id: refId }),
    ...(refType === 'place' && refId && { place_id: refId }),
    ...(params.googlePlaceId && { google_place_id: params.googlePlaceId }),
    ...(params.locationName && { location_name: params.locationName }),
    ...(params.contentType && { content_type: params.contentType }),
    ...(referralCreatorId && { referring_creator_id: referralCreatorId }),
  };
}

/** Fire Experience Outbound Click without opening a tab (use before auth gates). */
export function trackGygOutboundClick(params: OpenGygDeepLinkParams): void {
  if (!isGygExperiencesEnabled()) {
    return;
  }

  const searchQuery = params.searchQuery?.trim();
  if (!searchQuery) {
    return;
  }

  WebAnalytics.track(
    EVENT_EXPERIENCE_OUTBOUND_CLICK,
    buildGygOutboundAnalyticsProps(params, searchQuery),
  );
}

export function useOpenGygDeepLink(): {
  openGygDeepLink: (params: OpenGygDeepLinkParams) => void;
  isOpening: boolean;
  blockedUrl: string | undefined;
  clearBlockedState: () => void;
} {
  const [isOpening, setIsOpening] = useState(false);
  const [blockedUrl, setBlockedUrl] = useState<string | undefined>(undefined);

  const openGygDeepLink = useCallback((params: OpenGygDeepLinkParams) => {
    if (!isGygExperiencesEnabled()) {
      return;
    }

    const searchQuery = params.searchQuery?.trim();
    if (!searchQuery) {
      toast.error('Unable to find experiences for this location.');
      return;
    }

    setIsOpening(true);
    setBlockedUrl(undefined);

    const analyticsProps = buildGygOutboundAnalyticsProps(params, searchQuery);
    const cmp = analyticsProps.cmp;

    const url = buildGygDeepLinkUrl({ searchQuery, cmp });
    if (!url) {
      toast.error('Experiences are temporarily unavailable. Please try again later.');
      setIsOpening(false);
      return;
    }

    if (!params.skipAnalytics) {
      WebAnalytics.track(EVENT_EXPERIENCE_OUTBOUND_CLICK, analyticsProps);
    }

    openGygInNewTab({
      url,
      onSuccess: () => {
        setIsOpening(false);
      },
      onBlocked: (blocked) => {
        WebAnalytics.track(EVENT_EXPERIENCE_OUTBOUND_POPUP_BLOCKED, {
          surface: params.surface,
          vendor: 'gyg',
          has_url: !!blocked,
        });
        setBlockedUrl(blocked);
        setIsOpening(false);
      },
    });
  }, []);

  const clearBlockedState = useCallback(() => {
    setBlockedUrl(undefined);
  }, []);

  return { openGygDeepLink, isOpening, blockedUrl, clearBlockedState };
}
